import { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { Modal, ModalBody } from "reactstrap";
import { toast } from "react-hot-toast";
import { sortBy } from "lodash";

import { useUsersActions } from "../../../../services/actions/users.actions";
import { crudUserAtom } from "../../../../services/atoms/users.atoms";
import { ButtonLoading } from "../../../../components/buttons/button-loading";
import ClientAPI from "../../../../services/api/clients.api";

const ROLES = [
  { value: "superAdmin", label: "Super administrador (equipo GrowMe)" },
  { value: "clientAdmin", label: "Administrador de un cliente" },
  { value: "clientBasic", label: "Usuario básico de un cliente" },
];

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

// Invitar usuario: nadie elige ni ve contraseñas. El back crea la cuenta y le
// envía a la persona un link (válido 7 días) para que cree la suya con un
// código de verificación que llega a su correo.
const InviteUserModal = (props) => {
  const { open, toggle, onInvited } = props;
  const { createUser, inviteClientUser, resetUserCrud } = useUsersActions();
  const userResolver = useRecoilValue(crudUserAtom);
  const [role, setRole] = useState("superAdmin");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [result, setResult] = useState(null);

  const isClientRole = role !== "superAdmin";
  const sortedClients = useMemo(() => sortBy(clients, (c) => (c.name || "").toLowerCase()), [clients]);

  useEffect(() => () => resetUserCrud(), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isClientRole || clients.length) return;
    ClientAPI.getClientsList()
      .then(({ data }) => setClients(Array.isArray(data) ? data : []))
      .catch(() => toast.error("No se pudo cargar la lista de clientes"));
  }, [isClientRole, clients.length]);

  const finish = (email, response) => {
    const invited = response?.data?.invited !== false;
    setResult({ email, invited, message: response?.data?.message });
    onInvited && onInvited();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = Object.fromEntries(new FormData(e.target).entries());
    const name = `${form.name} ${form.surname || ""}`.trim();
    const digits = (form.phoneNumber || "").replace(/\D/g, "");
    const phoneNumber = digits ? `+${digits}` : "";

    if (!isClientRole) {
      createUser({ name: form.name, surname: form.surname, email: form.email, phoneNumber }, (response) => {
        if (response?.response?.status >= 400 || response instanceof Error) return; // createUser ya mostró el error
        finish(form.email, response);
      });
      return;
    }

    const client = clients.find((c) => c.id === clientId);
    if (!client) {
      toast.error("Elige el cliente al que pertenece el usuario");
      return;
    }
    try {
      const response = await inviteClientUser({
        name,
        email: form.email,
        phoneNumber,
        clientId: client.id,
        clientName: client.name,
        isAdmin: role === "clientAdmin",
      });
      finish(form.email, response);
    } catch (error) {
      toast.error(errorMessage(error, "No se pudo invitar al usuario"), { duration: 4000 });
    }
  };

  if (result) {
    return (
      <Modal isOpen={open} toggle={toggle} backdrop="static">
        <div className="gm-modal-header">
          <div className="gm-modal-title-container">
            <div className="gm-modal-title">{result.invited ? "Invitación enviada" : "Usuario creado"}</div>
            <div className="gm-modal-subtitle">
              {result.invited
                ? `Enviamos un correo a ${result.email} para que active su cuenta y cree su contraseña. El link vence en 7 días.`
                : result.message || "No pudimos enviar el correo. Reenvía la invitación desde la lista de usuarios."}
            </div>
          </div>
        </div>
        <ModalBody>
          <div className="gm-modal-buttons">
            <button type="button" className="gm-primary-button" onClick={toggle}>
              Listo
            </button>
          </div>
        </ModalBody>
      </Modal>
    );
  }

  return (
    <Modal isOpen={open} toggle={toggle}>
      <div className="gm-modal-header">
        <div className="gm-modal-title-container">
          <div className="gm-modal-title">Invitar usuario</div>
          <div className="gm-modal-subtitle">
            Le enviaremos un correo para que active su cuenta y cree su propia contraseña.
          </div>
        </div>
        <button type="button" className="btn-close" aria-label="Close" onClick={toggle} />
      </div>
      <ModalBody>
        <form onSubmit={handleSubmit}>
          <div className="gm-modal-form-container">
            <div className="gm-modal-form-section">
              <div className="gm-modal-form-group gm-wide-group">
                <div className="gm-modal-input-group">
                  <label htmlFor="role">Rol</label>
                  <select id="role" name="role" value={role} onChange={(e) => setRole(e.target.value)}>
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {isClientRole && (
                <div className="gm-modal-form-group gm-wide-group">
                  <div className="gm-modal-input-group">
                    <label htmlFor="clientId">Cliente</label>
                    <select id="clientId" name="clientId" value={clientId} onChange={(e) => setClientId(e.target.value)} required>
                      <option value="">Selecciona un cliente</option>
                      {sortedClients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <div className="gm-modal-form-group">
                <div className="gm-modal-input-group">
                  <label htmlFor="name">Nombre</label>
                  <input type="text" id="name" name="name" required />
                </div>
                <div className="gm-modal-input-group">
                  <label htmlFor="surname">Apellido</label>
                  <input type="text" id="surname" name="surname" required />
                </div>
              </div>
              <div className="gm-modal-form-group gm-wide-group">
                <div className="gm-modal-input-group">
                  <label htmlFor="email">Correo</label>
                  <input type="email" id="email" name="email" required />
                </div>
              </div>
              <div className="gm-modal-form-group gm-wide-group">
                <div className="gm-modal-input-group">
                  <label htmlFor="phoneNumber">Teléfono (opcional)</label>
                  <input type="text" id="phoneNumber" name="phoneNumber" maxLength={20} placeholder="569 1234 5678" />
                </div>
              </div>
            </div>
          </div>
          <div className="gm-modal-buttons">
            <button type="button" className="gm-secondary-button" onClick={toggle}>
              Cancelar
            </button>
            <ButtonLoading
              className="gm-primary-button"
              type="submit"
              buttonLabel="Enviar invitación"
              disabled={userResolver.loading}
              loading={userResolver.loading}
            />
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default InviteUserModal;
